import { useState, useEffect } from "react";
import { auth, db } from "../../firebase";
import { signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup, getAdditionalUserInfo, signOut, setPersistence, browserLocalPersistence, browserSessionPersistence } from "firebase/auth";
import { doc, setDoc, collection, query, where, getDocs, serverTimestamp } from "firebase/firestore";
import { Loader2 } from "lucide-react";
import { Button } from "../ui/Button";
import FormInput from "../ui/FormInput";
import { getLoginLockoutSeconds, recordFailedLogin, clearLoginAttempts } from "@/utils/loginRateLimit";
const GoogleIcon = () => <svg className="w-5 h-5" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"></path><path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path><path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"></path><path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571l6.19,5.238C44.438,36.338,48,30.656,48,24C48,22.659,47.862,21.35,47.611,20.083z"></path></svg>;

export default function LoginForm({ onSuccess, onSwitchToRegister, onSwitchToReset }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [lockoutSeconds, setLockoutSeconds] = useState(0);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    const existingLockout = getLoginLockoutSeconds(email);
    if (existingLockout > 0) {
      setLockoutSeconds(existingLockout);
      setError(`Too many failed attempts. Try again in ${existingLockout}s.`);
      return;
    }

    setLoading(true);
    try {
      // "Remember me" off signs out when the browser/tab closes instead of
      // staying signed in indefinitely — persistence must be set before the
      // actual sign-in call, it doesn't retroactively apply to an existing
      // session.
      await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
      const cred = await signInWithEmailAndPassword(auth, email, password);
      if (!cred.user.emailVerified) {
        await signOut(auth);
        throw new Error("Email not verified. Please check your inbox.");
      }
      clearLoginAttempts(email);
      onSuccess();
    } catch (err) {
      // Only count actual bad-credential attempts against the lockout —
      // not e.g. a network error or the unverified-email case above, which
      // aren't guessing attempts.
      if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential" || err.code === "auth/user-not-found") {
        const locked = recordFailedLogin(email);
        if (locked) {
          setLockoutSeconds(locked);
          setError(`Too many failed attempts. Try again in ${locked}s.`);
          setLoading(false);
          return;
        }
      }
      setError(err.message.replace("Firebase: ", ""));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");
    setLoading(true);
    const provider = new GoogleAuthProvider();
    try {
      await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
      const result = await signInWithPopup(auth, provider);
      const additionalUserInfo = getAdditionalUserInfo(result);

      if (additionalUserInfo?.isNewUser) {
        // Unlike email/password registration, there's no in-flow nickname
        // input to reject on collision here — the name comes straight from
        // the Google account. Rather than block sign-in on a name clash,
        // disambiguate automatically (RegisterForm.jsx handles the
        // interactive case by rejecting duplicates directly).
        const baseUsername = result.user.displayName || result.user.email?.split('@')[0] || "Player";
        let username = baseUsername;
        const usersRef = collection(db, 'users');
        const existing = await getDocs(query(usersRef, where('username', '==', username)));
        if (!existing.empty) {
          username = `${baseUsername}${Math.floor(1000 + Math.random() * 9000)}`;
        }

        await setDoc(doc(db, "users", result.user.uid), {
          username,
          email: result.user.email,
          photoURL: result.user.photoURL,
          wins: 0,
          losses: 0,
          createdAt: serverTimestamp(),
          displayId: crypto.randomUUID(),
          // Match RegisterForm.jsx so a fresh Google account appears on
          // leaderboards immediately instead of being invisible (Firestore's
          // orderBy excludes docs missing the ordered field) until they
          // happen to complete a ranked match in that exact mode/type combo.
          mmr_normal_words: 0,
          mmr_normal_words_punc: 0,
          mmr_normal_words_nums: 0,
          mmr_normal_words_punc_nums: 0,
          mmr_normal_quotes: 0,
          mmr_marathon_words: 0,
          mmr_marathon_words_punc: 0,
          mmr_marathon_words_nums: 0,
          mmr_marathon_words_punc_nums: 0,
          mmr_marathon_quotes: 0,
        });
      }
      onSuccess();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // Ticks the lockout countdown so the error message's "Try again in Ns"
  // stays accurate instead of freezing at whatever it said on the failed
  // attempt.
  useEffect(() => {
    if (lockoutSeconds <= 0) return;
    const interval = setInterval(() => {
      setLockoutSeconds((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [lockoutSeconds]);

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="text-center group">
        <h2 className="text-3xl font-black mb-1 text-white tracking-tighter uppercase italic bg-gradient-to-r from-indigo-400 via-purple-400 to-emerald-400 bg-clip-text text-transparent">
          Prepare to Type
        </h2>
        <p className="text-[9px] text-zinc-400 uppercase tracking-[0.3em] font-black">Identity.Initialize()</p>
      </div>

      {error && (
        <p className="bg-red-500/10 border border-red-500/20 text-red-400 text-xs text-center p-3 rounded-xl animate-pulse">
          {lockoutSeconds > 0
            ? `Too many failed attempts. Try again in ${lockoutSeconds}s.`
            : error}
        </p>
      )}

      {/* Main Form */}
      <form onSubmit={handleLogin} className="flex flex-col gap-4">
        <FormInput 
          label="Email Address"
          type="email"
          placeholder="email@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <FormInput 
          label="Password"
          type="password"
          placeholder="••••••••"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-[10px] text-zinc-400 uppercase tracking-wider font-bold cursor-pointer select-none">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="w-3.5 h-3.5 rounded-sm accent-indigo-500 cursor-pointer"
            />
            Remember Me
          </label>
          <button type="button" onClick={onSwitchToReset} className="text-[10px] text-zinc-400 hover:text-zinc-300 uppercase tracking-wider font-bold">Forgot Password?</button>
        </div>

        <Button
          type="submit"
          loading={loading}
          disabled={lockoutSeconds > 0}
          size="lg"
          className="w-full mt-2"
        >
          Begin Battle
        </Button>
      </form>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-zinc-800" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-zinc-900 px-4 text-[10px] uppercase font-black tracking-[0.2em] text-zinc-400">Secure Link</span>
        </div>
      </div>

      {/* Social Login */}
      <Button
        onClick={handleGoogleSignIn} 
        loading={loading}
        variant="secondary"
        size="lg"
        className="w-full !normal-case gap-3"
      >
        <GoogleIcon />
        Connect Google
      </Button>

      {/* Footer Switch */}
      <div className="text-center pt-2">
        <p className="text-[10px] text-zinc-400 uppercase tracking-widest font-bold">
          New to the arena?{" "}
          <Button
            type="button"
            onClick={onSwitchToRegister} 
            variant="link"
            className="ml-1"
          >
            Create Identity
          </Button>
        </p>
      </div>
    </div>
  );
}